import { Trans } from '@lingui/macro'
import { Box, Button, Typography } from '@mui/material'
import React from 'react'
import CustomizableLogo from './views/common-components/CustomizableLogo'

// Update this URL to your actual website
const WEBSITE_URL = 'https://centre.support/'

interface Props {
  startDate?: string | null
  endDate?: string | null
  salesforceOutage?: boolean
}

const MaintenanceScreen: React.FC<Props> = ({ startDate, endDate, salesforceOutage = false }) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '34px',
          backgroundImage: 'url("/assets/images/chtc-half-logo-small-spaces.png")',
          backgroundSize: 'auto',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat-x'
        }}
      />

      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '45px'
        }}
      >
        <Box
          component='img'
          src='/assets/images/centre-logo.svg'
          alt='My Centre'
          sx={{
            height: '48px',
            mr: 1
          }}
        />
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: '#1a3a52',
            fontSize: '28px',
            lineHeight: '32px'
          }}
        >
          <Trans>MY_CENTER</Trans>
        </Typography>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          backgroundColor: 'white',
          padding: '48px',
          pt: '40px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          zIndex: 1,
          position: 'relative'
        }}
      >
        {/* Decorative logo shapes */}
        <CustomizableLogo
          color='#9AC765'
          opacity={0.3}
          sx={{ position: 'absolute', top: '-47px', left: '-194px', width: '194px', height: 'auto' }}
        />

        <Box sx={{ minHeight: '170px' }}>
          {/* Heading */}
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              color: '#1a3a52',
              mb: '32px',
              fontSize: '24px',
              lineHeight: '28px'
            }}
          >
            {salesforceOutage
              ? 'Unexpected outage'
              : <Trans>SCHEDULED_MAINTENANCE_LABEL</Trans>}
          </Typography>

          {/* Body text */}
          <Typography
            variant='body1'
            sx={{
              lineHeight: '20px',
              fontSize: '14px',
              mb: 1,
              textAlign: 'left'
            }}
          >
            {salesforceOutage
              ? (
                <>
                  <b>Salesforce</b> is currently experiencing an outage that is actively being resolved. During this period, you are not able to login to <b>My Centre</b>.
                </>
                )
              : (
                <>
                  <b><Trans>MY_CENTER</Trans></b>{' '}
                  <Trans>IS_UNDERGOING_FRAGMENT</Trans>{' '}
                  <b><Trans>SCHEDULED_MAINTENANCE</Trans></b>{' '}
                  <Trans>FROM_FRAGMENT</Trans>{' '}
                  {startDate}{' '}
                  <Trans>TO_FRAGMENT</Trans>{' '}
                  {endDate}{'. '}
                  <Trans>NOT_ABLE_TO_LOGIN_FRAGMENT</Trans>{' '}
                  <b><Trans>MY_CENTER</Trans></b>.
                </>
                )}
          </Typography>

          <Typography
            variant='body1'
            sx={{
              mb: '24px',
              textAlign: 'left',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '20px'
            }}
          >
            {salesforceOutage
              ? 'We thank you for your understanding!'
              : <Trans>MAINTENANCE_SCREEN_WISHING_MESSAGE</Trans>}
          </Typography>
        </Box>

        {/* Button */}
        <a href={WEBSITE_URL}>
          <Button
            variant='contained'
            fullWidth
          >
            <Trans>GO_TO_WEBSITE_BTN</Trans>
          </Button>
        </a>
      </Box>
    </Box>
  )
}

export default MaintenanceScreen
